import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import ButtonsPagination from '@/components/ButtonsPaginations'
import StatusChip from '@/components/StatusChip'
import { HELP_TEXT } from '@/components/StatusChip/config'
import CaluButton from '@/components/styledComponents/CaluButton'
import { SELECTED_TURN_REMINDER } from '@/store/wapp'
import { getWappTurnReminders, setSentRemindersQuantity } from '@/store/wapp/actions'
import { handleSetCountryCode } from '@/utils/functions/handleSetCountryCode'
import { handleWappReminder } from '@/utils/functions/handleWappReminders'
import { toCustomTz } from '@/utils/functions/toCustomTz'
import SendIcon from '@mui/icons-material/Send'
import { Box, Tooltip } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { esES } from '@mui/x-data-grid/locales'

import dayjs from 'dayjs'

import s from './styles.module.scss'

const ManagementTable = ({ startDate, finishDate, deviceId, setOpen, filteredTurnRemindersAvailable }) => {
  const dispatch = useDispatch()
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [selectedButton, setSelectedButton] = useState(null)
  const { isLoading, paginationData, status: wappStatus } = useSelector((state) => state?.wapp)
  const { centreProfile } = useSelector((state) => state?.centres)
  const { totalDocs } = paginationData || {}
  const { usersProfileConnected } = useSelector((state) => state.users)
  const { isAdmin } = usersProfileConnected
  const { t } = useTranslation('global')

  const handleSendWhatsApp = async (data, buttonId) => {
    setSelectedButton(buttonId)
    setIsLoadingButton(true)
    const selectedTurn = data.value
    let auxData = {
      selectedTurn: {
        ...selectedTurn,
        centre: centreProfile,
      },
      state: 'init',
      dispatch: dispatch,
      wappStatus: wappStatus,
    }

    try {
      await handleWappReminder(auxData)
      await dispatch(setSentRemindersQuantity(1))
    } catch (e) {
      console.log(e)
    } finally {
      setIsLoadingButton(false)
    }
  }

  const renderMessageType = (params) => {
    const attempts = params.value
    return (
      <>
        {!attempts && <div className={s.noData}>-</div>}
        <div>
          {attempts?.map((a) => (
            <div key={a._id} className={s.messageType}>
              {a.messageType}
            </div>
          ))}
        </div>
      </>
    )
  }

  const renderDateReminder = (params) => {
    const raw = params.value
    const attempts = (() => {
      if (!raw) return []
      if (Array.isArray(raw)) return raw // [{ date, ... }]
      if (typeof raw === 'string')
        return [{ _id: 'single', statusChangedDate: toCustomTz(raw, undefined, true, 'DD/MM/YYYY HH:mm') }] // ISO string suelto
      return []
    })()

    if (attempts.length === 0) {
      return <div className={s.noData}>—</div>
    }

    return (
      <div className={s.dateList}>
        {attempts.map((a, i) => (
          <div key={a._id ?? i} className={s.dateChip}>
            <span className={s.dateChipDay}>{dayjs(a?.statusChangedDate).format('DD-MM-YYYY')}</span>
            <span className={s.dateChipSep} />
            <span className={s.dateChipTime}>{dayjs(a?.statusChangedDate).format('HH:mm')} hs</span>
          </div>
        ))}
      </div>
    )
  }

  const renderStatus = (params) => {
    const attempts = params.value
    return (
      <div>
        {!attempts && (
          <div className={s.iconContainer}>
            <StatusChip status={'queued'} />
            <span className={s.helpText}>{`${HELP_TEXT['queued']}`}</span>
          </div>
        )}
        {attempts?.map((a) => (
          <div className={s.iconContainer} key={a._id}>
            <StatusChip status={a.status} />
            {HELP_TEXT[a.status] && <div className={s.helpText}>{HELP_TEXT[a.status]}</div>}
          </div>
        ))}
      </div>
    )
  }

  const handleCellClick = async (params, event) => {
    if (event.target.id === 'sendReminderButton') return
    await dispatch(SELECTED_TURN_REMINDER(params?.row))
    setOpen(true)
  }

  const stylesColumns = {
    headerClassName: 'header',
    alignItems: 'flex-start',
    flex: 1,
  }

  const columns = [
    {
      field: 'fullName',
      headerName: 'Nombre',
      ...stylesColumns,
      renderCell: (params) => <strong>{params.value}</strong>,
    },

    {
      field: 'mobile',
      headerName: 'Teléfono',
      ...stylesColumns,
    },
    {
      field: 'turnDate',
      headerName: `Fecha de ${t('turnLabel.s')}`,
      ...stylesColumns,
      renderCell: (params) => renderDateReminder(params),
    },
    {
      field: 'messageType',
      headerName: 'Tipo de mensaje',

      ...stylesColumns,
      renderCell: (params) => renderMessageType(params),
    },
    {
      field: 'date',
      headerName: 'Fecha de envío',
      ...stylesColumns,
      renderCell: (params) => renderDateReminder(params),
    },
    {
      field: 'status',
      headerName: 'Estado',
      ...stylesColumns,
      renderCell: (params) => renderStatus(params),
    },
    {
      field: 'raw',
      headerName: '',
      maxWidth: 160,
      ...stylesColumns,
      renderCell: (params) => renderSendButton(params),
    },
  ]

  const rows = filteredTurnRemindersAvailable?.map((turn) => {
    const patientCentre = turn.patientCentre
    const countryCode = handleSetCountryCode(
      patientCentre?.country || centreProfile?.country || import.meta.env.REACT_APP_COUNTRY,
    )
    const patientMobile = patientCentre?.mobile
    const attempts = turn.wappReminder?.attempts
    return {
      id: turn?._id,
      fullName: `${patientCentre?.lastname?.toUpperCase()}, ${patientCentre?.firstname?.toUpperCase()}`,
      mobile: patientMobile ? countryCode + patientMobile : 'Celular sin registrar',
      turnDate: turn?.startHour,
      attempts: attempts,
      messageType: attempts,
      date: attempts,
      status: attempts,
      raw: turn,
    }
  })

  const handleChangePaginationNumber = (page) => {
    let params = {
      centre: deviceId,
      start: toCustomTz(startDate, 'utc', true),
      finish: toCustomTz(finishDate, 'utc', true),
      page: page,
      limit: 20,
    }
    dispatch(getWappTurnReminders(params))
  }

  const hasLastAttemptWithError = (attempts) => {
    const lastAttempt = attempts[attempts?.length - 1]
    return lastAttempt?.attemptTriesCount >= 3 && lastAttempt?.status === 'error'
  }

  const renderSendButton = (params) => {
    const attemps = params?.row?.attempts || []
    const hasError = hasLastAttemptWithError(attemps)
    const textButton = hasError ? 'Reenviar' : 'Enviar'
    const isLoading = selectedButton === params.id ? isLoadingButton : false

    return (
      <div className={s.sendButtonContainer}>
        <Tooltip title={textButton} placement="top">
          <CaluButton
            onClick={() => handleSendWhatsApp(params, params.id)}
            id="sendReminderButton"
            disable={isAdmin}
            size={'small'}
            loading={isLoading}
            startIcon={<SendIcon />}
            className={hasError ? s.resendButton : s.sendButton}
          >
            {textButton}
          </CaluButton>
        </Tooltip>
      </div>
    )
  }

  return (
    <Box
      sx={{
        px: 0,
        '& .header': {
          fontSize: '14px',
        },
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
          background: '#fff',
        },
      }}
    >
      <DataGrid
        initialState={{
          sorting: {
            sortModel: [{ field: 'fullname', sort: 'asc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        rows={rows || []}
        rowCount={totalDocs || 0}
        getRowHeight={() => 'auto'}
        loading={isLoading}
        columns={columns}
        disableRowSelectionOnClick={true}
        onCellClick={(params, event) => handleCellClick(params, event)}
        rowClassName={s.rowClassName}
        autoHeight={true}
        hideFooter={true}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          fontSize: '13px',
          padding: '10px',
          marginBottom: '30px',
          background: '#fff',
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
          '&.MuiDataGrid-root .MuiDataGrid-cell ': {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            margin: '10px 0px 10px 0px',
          },
        }}
      />
      {filteredTurnRemindersAvailable?.length >= 1 && (
        <div className={s.paginationButton}>
          <ButtonsPagination
            paginationData={paginationData}
            handleChangePaginationNumber={handleChangePaginationNumber}
          />
        </div>
      )}
    </Box>
  )
}

export default ManagementTable
